import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator, Image, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { ScreenLayout, Header, FormHeader, Button } from '../components';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService } from '../services/api';
import { DatePickerField } from '../components/DatePickerField';
import { EmojiPicker } from '../components/EmojiPicker';
import { PostCard } from '../components/PostCard';
import { colors, spacing, typography } from '../theme';
import { withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { takePostToEdit } from '../services/postEditing';
import type { RecentPost } from './dashboard/RecentPostsCard';

export interface PostCreateScreenProps {
  pageId?: string;
}

interface Page { page_id: string; page_name?: string }
interface MediaItem { uri: string; url?: string; uploading: boolean }

const MAX_PHOTOS = 10;
const MAX_CHARS = 63206; // Facebook's post text limit
const MIN_AHEAD_MS = 10 * 60 * 1000;
const MAX_AHEAD_MS = 75 * 24 * 60 * 60 * 1000;

/**
 * Compose a Facebook page post: one or several pages, text (emoji,
 * hashtags), up to 10 photos or one video, or a link; publish now,
 * schedule, or save as a draft; with a live preview. Also edits a post
 * handed over by the Posts screen (routeParams.postId) — a published post
 * only its text, as Facebook allows.
 */
export const PostCreateScreen: React.FC<PostCreateScreenProps> = ({ pageId: pageIdProp }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const editing = useMemo<RecentPost | null>(() => takePostToEdit((routeParams as any)?.postId), [(routeParams as any)?.postId]);
  const textOnly = editing?.status === 'published';
  const initialPageId = editing?.pageId || pageIdProp || (routeParams as any)?.pageId;

  const [pages, setPages] = useState<Page[]>([]);
  const [pageIds, setPageIds] = useState<string[]>(initialPageId ? [initialPageId] : []);
  const [message, setMessage] = useState(editing?.message || '');
  const [mediaType, setMediaType] = useState<'photo' | 'video'>(editing?.mediaType === 'video' ? 'video' : 'photo');
  const [media, setMedia] = useState<MediaItem[]>((editing?.mediaUrls || []).map((url) => ({ uri: url, url, uploading: false })));
  const [linkUrl, setLinkUrl] = useState(editing?.linkUrl || '');
  const [showLink, setShowLink] = useState(!!editing?.linkUrl);
  const [scheduled, setScheduled] = useState(!!editing?.scheduledFor && editing.status !== 'published');
  const [scheduledFor, setScheduledFor] = useState<Date | null>(editing?.scheduledFor ? new Date(editing.scheduledFor) : null);
  const [submitting, setSubmitting] = useState<'publish' | 'draft' | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const selectionRef = useRef({ start: 0, end: 0 });

  useEffect(() => {
    apiService.getFacebookPages()
      .then((res) => {
        const list: Page[] = Array.isArray(res.data) ? res.data : [];
        setPages(list);
        setPageIds((prev) => (prev.length ? prev : list[0]?.page_id ? [list[0].page_id] : []));
      })
      .catch(() => setPages([]));
  }, []);

  const uploading = media.some((m) => m.uploading);
  const hasContent = !!message.trim() || media.length > 0 || !!linkUrl.trim();
  const scheduleProblem = (() => {
    if (!scheduled) return null;
    if (!scheduledFor) return t('posts.composer.pickTime');
    const ahead = scheduledFor.getTime() - Date.now();
    if (ahead < MIN_AHEAD_MS) return t('posts.composer.tooSoon');
    if (ahead > MAX_AHEAD_MS) return t('posts.composer.tooFar');
    return null;
  })();

  const togglePage = (id: string) => {
    if (editing) return; // an existing post belongs to its page
    setPageIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const upload = async (item: MediaItem, kind: 'photo' | 'video') => {
    try {
      const fileName = `post_${Date.now()}_${Math.random().toString(36).slice(2, 6)}.${kind === 'video' ? 'mp4' : 'jpg'}`;
      const res = await apiService.uploadMedia(item.uri, fileName, kind === 'video' ? 'video/mp4' : 'image/jpeg', 'default-facebook-messenger');
      const url = res.statusCode === 1000 ? res.data?.mediaUrl : undefined;
      if (!url) throw new Error();
      setMedia((prev) => prev.map((m) => (m.uri === item.uri ? { ...m, url, uploading: false } : m)));
    } catch {
      setMedia((prev) => prev.filter((m) => m.uri !== item.uri));
      showNotification('error', t('posts.composer.uploadFailed'), '');
    }
  };

  const pick = async (kind: 'photo' | 'video', source: 'library' | 'camera' = 'library') => {
    const permission = source === 'library'
      ? await ImagePicker.requestMediaLibraryPermissionsAsync()
      : await ImagePicker.requestCameraPermissionsAsync();
    if (!permission.granted) {
      showNotification('warning', t('business.photoPermissionTitle'), t('business.photoPermissionBody'));
      return;
    }
    const room = kind === 'photo' ? MAX_PHOTOS - (mediaType === 'photo' ? media.length : 0) : 1;
    const launch = source === 'library' ? ImagePicker.launchImageLibraryAsync : ImagePicker.launchCameraAsync;
    const result = await launch({
      mediaTypes: kind === 'video' ? ImagePicker.MediaTypeOptions.Videos : ImagePicker.MediaTypeOptions.Images,
      allowsMultipleSelection: kind === 'photo' && source === 'library',
      selectionLimit: kind === 'photo' ? room : 1,
      quality: 0.8,
    });
    if (result.canceled || !result.assets?.length) return;
    const picked = result.assets.slice(0, room).map((a) => ({ uri: a.uri, uploading: true }));
    // Photos and a video don't mix: switching type replaces what was there.
    const base = mediaType === kind ? media : [];
    setMediaType(kind);
    setShowLink(false);
    setLinkUrl('');
    setMedia(kind === 'video' ? picked : [...base, ...picked].slice(0, MAX_PHOTOS));
    picked.forEach((item) => upload(item, kind));
  };

  const insertAtCursor = (text: string) => {
    const { start, end } = selectionRef.current;
    setMessage((prev) => prev.slice(0, start) + text + prev.slice(end));
  };

  const failMessage = (res: any) => (res?.additionalInfo?.errorCode === 'missing_permissions'
    ? t('posts.error.permissions')
    : (res?.errors?.[0]?.message || ''));

  const submit = async (asDraft: boolean) => {
    if (!hasContent || uploading || pageIds.length === 0) return;
    if (!asDraft && scheduleProblem) {
      showNotification('error', t('posts.composer.title'), scheduleProblem);
      return;
    }
    setSubmitting(asDraft ? 'draft' : 'publish');
    const content = {
      message: message.trim(),
      mediaUrls: media.map((m) => m.url!).filter(Boolean),
      mediaType: media.length ? mediaType : undefined,
      linkUrl: !media.length && linkUrl.trim() ? linkUrl.trim() : undefined,
      scheduledFor: scheduled && scheduledFor ? scheduledFor.toISOString() : undefined,
    };
    try {
      let res: any;
      if (editing) {
        res = textOnly
          ? await apiService.updateFacebookPost(editing.postId, { message: content.message })
          : await apiService.updateFacebookPost(editing.postId, { ...content, linkUrl: content.linkUrl ?? null, scheduledFor: content.scheduledFor ?? null });
        // A draft/failed post goes out on "Publish"/"Schedule" after saving it
        if (res.statusCode === 1000 && !asDraft && (editing.status === 'draft' || editing.status === 'failed')) {
          res = await apiService.publishFacebookPost(res.data?.postId || editing.postId);
        }
      } else {
        res = await apiService.createFacebookPosts({ pageIds, ...content, asDraft });
      }
      if (res.statusCode !== 1000) {
        showNotification('error', t('posts.composer.failed'), failMessage(res));
        return;
      }
      const failed = res.data?.failed || 0;
      if (failed > 0) {
        showNotification('warning', t('posts.composer.partial', { count: failed }), t('posts.composer.partialHint'));
      } else {
        showNotification('success', t(asDraft ? 'posts.done.draftSaved' : editing && textOnly ? 'posts.done.updated' : scheduled ? 'posts.done.scheduled' : 'posts.done.published'), '');
      }
      navigateTo('posts-list', undefined, { status: asDraft ? 'draft' : scheduled ? 'scheduled' : failed ? 'failed' : 'published' });
    } catch (err: any) {
      showNotification('error', t('posts.composer.failed'), err?.message || '');
    } finally {
      setSubmitting(null);
    }
  };

  const previewPage = pages.find((p) => p.page_id === pageIds[0]);
  const preview: RecentPost = {
    postId: 'preview',
    message: message.trim() || undefined,
    mediaUrls: media.map((m) => m.uri),
    mediaType: media.length ? mediaType : linkUrl ? 'link' : 'none',
    linkUrl: !media.length && linkUrl.trim() ? linkUrl.trim() : undefined,
    status: scheduled ? 'scheduled' : 'published',
    scheduledFor: scheduled && scheduledFor ? scheduledFor.toISOString() : undefined,
    createdAt: new Date().toISOString(),
    pageName: previewPage?.page_name,
  } as RecentPost;

  const publishLabel = editing && textOnly
    ? t('posts.composer.saveChanges')
    : scheduled ? t('posts.composer.schedule') : t('posts.composer.publish');

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title={t(editing ? 'posts.composer.editTitle' : 'posts.composer.title')} onBack={() => navigateTo('posts-list')} />}
    >
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        {/* Pages */}
        <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t(editing ? 'posts.composer.page' : 'posts.composer.pages')}</Text>
        {pages.length === 0 ? (
          <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('posts.noPages')}</Text>
        ) : (
          <View style={[styles.chips, { flexDirection: rtl.rowDirection }]}>
            {pages.filter((p) => !editing || p.page_id === editing.pageId).map((p) => {
              const on = pageIds.includes(p.page_id);
              return (
                <TouchableOpacity
                  key={p.page_id}
                  style={[styles.chip, on && styles.chipOn, { flexDirection: rtl.rowDirection }]}
                  onPress={() => togglePage(p.page_id)}
                  disabled={!!editing}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: on }}
                >
                  <Ionicons name={on ? 'checkmark-circle' : 'logo-facebook'} size={16} color={(on ? colors.primary : '#1877F2') as string} />
                  <Text style={[styles.chipText, on && styles.chipTextOn]} numberOfLines={1}>{p.page_name || p.page_id}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        {/* Text */}
        <View style={styles.editor}>
          <TextInput
            style={[styles.textArea, { textAlign: rtl.textStart }]}
            value={message}
            onChangeText={(v) => setMessage(v.slice(0, MAX_CHARS))}
            onSelectionChange={(e) => { selectionRef.current = e.nativeEvent.selection; }}
            placeholder={t('posts.composer.placeholder')}
            placeholderTextColor={colors.text.secondary as string}
            multiline
            textAlignVertical="top"
          />
          <View style={[styles.toolbar, { flexDirection: rtl.rowDirection }]}>
            {!textOnly && (
              <>
                <TouchableOpacity style={styles.tool} onPress={() => pick('photo')} accessibilityLabel={t('posts.composer.addPhotos')}>
                  <Ionicons name="images-outline" size={20} color={colors.primary as string} />
                </TouchableOpacity>
                {Platform.OS !== 'web' && (
                  <TouchableOpacity style={styles.tool} onPress={() => pick('photo', 'camera')} accessibilityLabel={t('posts.composer.camera')}>
                    <Ionicons name="camera-outline" size={20} color={colors.primary as string} />
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={styles.tool} onPress={() => pick('video')} accessibilityLabel={t('posts.composer.addVideo')}>
                  <Ionicons name="videocam-outline" size={20} color={colors.primary as string} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.tool}
                  onPress={() => { if (!media.length) setShowLink((v) => !v); }}
                  disabled={media.length > 0}
                  accessibilityLabel={t('posts.composer.addLink')}
                >
                  <Ionicons name="link-outline" size={20} color={(media.length ? colors.text.disabled : colors.primary) as string} />
                </TouchableOpacity>
              </>
            )}
            <TouchableOpacity style={styles.tool} onPress={() => setShowEmoji(true)} accessibilityLabel={t('posts.composer.emoji')}>
              <Ionicons name="happy-outline" size={20} color={colors.primary as string} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.tool} onPress={() => insertAtCursor('#')} accessibilityLabel="#">
              <Text style={styles.hash}>#</Text>
            </TouchableOpacity>
            <View style={styles.flex} />
            <Text style={styles.counter}>{message.length.toLocaleString()}</Text>
          </View>
        </View>

        {showLink && !media.length && !textOnly && (
          <TextInput
            style={[styles.linkInput, { textAlign: rtl.textStart }]}
            value={linkUrl}
            onChangeText={setLinkUrl}
            placeholder="https://"
            placeholderTextColor={colors.text.secondary as string}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
          />
        )}

        {media.length > 0 && (
          <View style={[styles.mediaGrid, { flexDirection: rtl.rowDirection }]}>
            {media.map((m) => (
              <View key={m.uri} style={styles.thumbWrap}>
                {mediaType === 'video' ? (
                  <View style={[styles.thumb, styles.videoThumb]}>
                    <Ionicons name="videocam" size={28} color="#FFFFFF" />
                  </View>
                ) : (
                  <Image source={{ uri: m.uri }} style={styles.thumb} />
                )}
                {m.uploading && (
                  <View style={styles.thumbOverlay}><ActivityIndicator color="#FFFFFF" /></View>
                )}
                {!textOnly && (
                  <TouchableOpacity style={styles.remove} onPress={() => setMedia((prev) => prev.filter((x) => x.uri !== m.uri))} hitSlop={6}>
                    <Ionicons name="close-circle" size={22} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            {mediaType === 'photo' && media.length < MAX_PHOTOS && !textOnly && (
              <TouchableOpacity style={[styles.thumb, styles.addThumb]} onPress={() => pick('photo')}>
                <Ionicons name="add" size={26} color={colors.primary as string} />
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* When */}
        {!textOnly && (
          <>
            <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('posts.composer.when')}</Text>
            <View style={[styles.segment, { flexDirection: rtl.rowDirection }]}>
              {[false, true].map((later) => (
                <TouchableOpacity
                  key={String(later)}
                  style={[styles.segmentItem, scheduled === later && styles.segmentOn]}
                  onPress={() => setScheduled(later)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: scheduled === later }}
                >
                  <Text style={[styles.segmentText, scheduled === later && styles.segmentTextOn]}>
                    {t(later ? 'posts.composer.later' : 'posts.composer.now')}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {scheduled && (
              <DatePickerField
                label={t('posts.composer.publishAt')}
                mode="datetime"
                value={scheduledFor}
                onChange={setScheduledFor}
                minimumDate={new Date(Date.now() + MIN_AHEAD_MS)}
                maximumDate={new Date(Date.now() + MAX_AHEAD_MS)}
                placeholder={t('posts.composer.pickTime')}
              />
            )}
            {scheduleProblem && scheduledFor && <Text style={styles.error}>{scheduleProblem}</Text>}
          </>
        )}

        {/* Preview */}
        {hasContent && (
          <>
            <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('posts.composer.preview')}</Text>
            <PostCard post={preview} compact={false} disableCommentsModal />
          </>
        )}

        <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
          {!textOnly && (
            <Button
              title={t('posts.composer.saveDraft')}
              variant="outline"
              onPress={() => submit(true)}
              loading={submitting === 'draft'}
              disabled={!hasContent || uploading || !!submitting || pageIds.length === 0}
              style={styles.actionButton}
            />
          )}
          <Button
            title={publishLabel}
            onPress={() => submit(false)}
            loading={submitting === 'publish'}
            disabled={!hasContent || uploading || !!submitting || pageIds.length === 0 || (!textOnly && !!scheduleProblem)}
            style={styles.actionButton}
          />
        </View>
        {pageIds.length > 1 && !editing && (
          <Text style={[styles.hint, { textAlign: 'center' }]}>{t('posts.composer.multiHint', { count: pageIds.length })}</Text>
        )}
      </ScrollView>

      <EmojiPicker visible={showEmoji} onEmojiSelect={insertAtCursor} onClose={() => setShowEmoji(false)} />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  scroll: { flex: 1 },
  content: { padding: spacing.md, gap: spacing.sm, maxWidth: 680, width: '100%', alignSelf: 'center', paddingBottom: spacing.xl * 2 },
  label: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, marginTop: spacing.sm },
  hint: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  error: { fontSize: typography.fontSize.sm, color: colors.error },
  chips: { flexWrap: 'wrap', gap: spacing.xs },
  chip: {
    alignItems: 'center',
    gap: 6,
    maxWidth: '100%',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
  },
  chipOn: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary as string, 0.08) },
  chipText: { fontSize: typography.fontSize.sm, color: colors.text.primary, flexShrink: 1 },
  chipTextOn: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  editor: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surfaceRaised,
    marginTop: spacing.sm,
  },
  textArea: {
    minHeight: 140,
    padding: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  } as any,
  toolbar: { alignItems: 'center', gap: 2, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderTopWidth: 1, borderTopColor: colors.border },
  tool: { padding: spacing.sm, borderRadius: 18 },
  hash: { fontSize: 18, fontFamily: typography.fontFamily.bold, color: colors.primary },
  flex: { flex: 1 },
  counter: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  linkInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    backgroundColor: colors.background.surfaceRaised,
  },
  mediaGrid: { flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.xs },
  thumbWrap: { position: 'relative' },
  thumb: { width: 92, height: 92, borderRadius: designTokens.borderRadius.md, backgroundColor: colors.background.secondary },
  videoThumb: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#111827' },
  addThumb: { alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderStyle: 'dashed', borderColor: colors.primary },
  thumbOverlay: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.35)', borderRadius: designTokens.borderRadius.md },
  remove: { position: 'absolute', top: 2, right: 2 },
  segment: { gap: 4, padding: 3, borderRadius: 10, backgroundColor: colors.background.secondary, alignSelf: 'flex-start', marginTop: spacing.xs },
  segmentItem: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderRadius: 8 },
  segmentOn: { backgroundColor: colors.background.surface },
  segmentText: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  segmentTextOn: { color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
  actionButton: { flex: 1 },
});
